import React, { useCallback, useContext, useMemo, useState } from 'react';
import classNames from 'classnames';
import { useForm } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import Question from '../Question';
import { Button, Loader } from 'ncoded-component-library';
import ArrowleftIcon from 'icons/Arrowleft.icon';
import useQuestions, { QuestionType } from '../../hooks/useQuestions';
import ProgressStepper from 'components/ProgressStepper';
import { scrollToErrors, scrollToTop } from 'utils';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';

import './DesktopVendor.styles.scss';
import './DesktopVendor.styles.responsive.scss';

type DesktopVendorProps = {
  className?: string;
};

const DesktopVendor: React.FC<DesktopVendorProps> = (props) => {
  const { className } = props;

  const { submit, getState } = useForm();
  const { t } = useTranslation();
  const { currentUser } = useContext(CurrentUserContext);
  const {
    firstStepQuestions,
    secondStepQuestions,
    thirdStepQuestions,
    allQuestions,
  } = useQuestions();

  const { invalid, submitting } = getState();

  const STEPS = useMemo(() => {
    return {
      0: {
        title: t('desktopVendorStep1Title'),
        description: t('desktopVendorStep1SubTitle'),
        submitText: t('desktopVendorStep1Submit'),
        stepperTitle: t('desktopVendorStep1StepperTitle'),
        questions: firstStepQuestions,
      },
      1: {
        title: t('desktopVendorStep2Title'),
        description: t('desktopVendorStep2SubTitle'),
        submitText: t('desktopVendorStep2Submit'),
        stepperTitle: t('desktopVendorStep2StepperTitle'),
        questions: secondStepQuestions,
      },
      2: {
        title: t(
          currentUser
            ? 'vendorQualificationFinalTitle'
            : 'desktopVendorStep3Title',
        ),
        description: t('desktopVendorStep3SubTitle'),
        submitText: t('desktopVendorStep3Submit'),
        stepperTitle: t('desktopVendorStep3StepperTitle'),
        questions: thirdStepQuestions,
      },
    };
  }, [
    currentUser,
    firstStepQuestions,
    secondStepQuestions,
    thirdStepQuestions,
    t,
  ]);

  const [currentStep, setCurrentStep] = useState<keyof typeof STEPS>(0);

  const stepsLength = Object.values(STEPS).length;
  const isLastStep = currentStep === stepsLength - 1;

  const RenderQuestions = useCallback(
    (groupOfQuestions: QuestionType[]) => {
      return groupOfQuestions.map((question, index) => {
        const Component = question.Component;
        return (
          <Question
            key={index}
            title={question.title}
            description={question.description}
            currentQuestionNum={currentStep * 5 + index + 1}
            numOfQuestions={allQuestions.length}
          >
            <Component />
          </Question>
        );
      });
    },
    [allQuestions, currentStep],
  );

  const classes = classNames(
    'desktop-vendor',
    { 'desktop-vendor--last-step': isLastStep },
    className,
  );

  const stepperSteps = useMemo(() => {
    return Object.entries(STEPS).map(([key, value]) => ({
      title: value.stepperTitle,
      onClick: () => setCurrentStep(+key as keyof typeof STEPS),
    }));
  }, [STEPS]);

  return (
    <div className={classes}>
      <ProgressStepper steps={stepperSteps} currentStep={currentStep} />
      <div className="desktop-vendor__content">
        {submitting && <Loader />}
        <div className="desktop-vendor__content__title">
          <h1>{STEPS[currentStep].title}</h1>
          <p>{STEPS[currentStep].description}</p>
        </div>
        {RenderQuestions(STEPS[currentStep].questions)}
      </div>
      <div className="desktop-vendor__actions">
        {!!currentStep && (
          <Button
            icon={ArrowleftIcon}
            variant="outline"
            onClick={() => {
              setCurrentStep((prev) => (prev - 1) as keyof typeof STEPS);
              scrollToTop();
            }}
          />
        )}
        <Button
          disabled={submitting}
          onClick={() => {
            if (isLastStep || invalid) {
              submit();
              scrollToErrors();
            } else {
              setCurrentStep((prev) => (prev + 1) as keyof typeof STEPS);
              scrollToTop();
            }
          }}
        >
          {STEPS[currentStep].submitText}
        </Button>
      </div>
    </div>
  );
};

export default DesktopVendor;
